import {
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { IconComponent } from '../../../shared/components/icon/icon.component';
import { FocusTrapDirective } from '../../../shared/directives/focus-trap/focus-trap.directive';
import { formatDobNumeric } from '../../../shared/utils/date-time/date-time.utils';
import { logHttpError } from '../../../shared/utils/logging/http-error-logging.utils';
import { RequestDetailPatient } from '../../models/request-detail';
import { PatientPortalProxyUserRequestsService } from '../../services/patient-portal-proxy-user-requests.service';
import {
  formatPatientAddress,
  formatPatientMrn,
  formatPatientPcp,
  formatPatientPhone,
} from '../../../shared/utils/patient-display/patient-display.utils';

/**
 * Confirmation step in front of granting a proxy access to a patient record.
 *
 * The PBI notes require both decisions to be confirmed with a modal prompt, and
 * Approve is the half that needs it most: the detail screen preselects the first
 * search hit, so without this a single click on a freshly loaded page would grant
 * a caregiver access to a record no human ever looked at. The record is restated
 * here in full — name, DOB, MRN, PCP, address, phone — so the confirmation is a
 * genuine check against the request rather than an "are you sure?".
 *
 * Owns the Approve call itself, mirroring `ProxyDenyRequestModal`: the modal that
 * takes the decision is the modal that reports whether it succeeded.
 */
@Component({
  selector: 'app-proxy-approve-request-modal',
  standalone: true,
  imports: [IconComponent, FocusTrapDirective],
  templateUrl: './proxy-approve-request-modal.html',
  styleUrl: './proxy-approve-request-modal.scss',
})
export class ProxyApproveRequestModal implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly requestsService = inject(PatientPortalProxyUserRequestsService);

  readonly requestId = input.required<number>();
  /** Concurrency token from the detail payload — a GUID here, not a number. */
  readonly version = input.required<string>();
  readonly patientName = input.required<string>();
  readonly proxyName = input.required<string>();
  readonly relationshipToPatient = input<string | null>(null);
  /** The Bolt record staff selected — the one this approval links. */
  readonly patient = input.required<RequestDetailPatient>();

  readonly cancelled = output<void>();
  readonly approved = output<void>();

  readonly isApprovingRequest = signal(false);
  readonly approveError = signal<string | null>(null);

  /** "Spouse of Sarah Thompson", or just the patient when no relationship is on file. */
  readonly relationshipSummary = computed(() => {
    const relationship = this.relationshipToPatient()?.trim();
    return relationship
      ? `${relationship} of ${this.patientName()}`
      : `Proxy for ${this.patientName()}`;
  });

  /** The selected record restated as label/value rows for the confirmation. */
  readonly recordFields = computed(() => {
    const patient = this.patient();
    return [
      { label: 'Date of Birth', value: formatDobNumeric(patient.dateOfBirth) },
      { label: 'MRN', value: formatPatientMrn(patient.medicalRecordNumber) },
      { label: 'PCP', value: formatPatientPcp(patient.primaryCareProvider) },
      { label: 'Address', value: formatPatientAddress(patient.address) },
      { label: 'Phone', value: formatPatientPhone(patient.phoneNumber) },
    ];
  });

  ngOnInit(): void {
    // Focus lands on Cancel, not Approve: the confirmation exists to interrupt a
    // reflex click, so the destructive-by-default option must not be one Enter away.
    setTimeout(() => {
      this.elementRef.nativeElement
        .querySelector<HTMLElement>('.proxy-approve-modal__cancel')
        ?.focus();
    });
  }

  cancel(): void {
    this.cancelled.emit();
  }

  submit(): void {
    if (this.isApprovingRequest()) return;

    this.isApprovingRequest.set(true);
    this.approveError.set(null);

    this.requestsService
      .approveRequest(this.requestId(), {
        patientId: this.patient().patientId,
        version: this.version(),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isApprovingRequest.set(false);
          this.approved.emit();
        },
        error: (error) => {
          logHttpError('Failed to approve proxy request', error);
          this.approveError.set('Unable to approve this request. Please try again.');
          this.isApprovingRequest.set(false);
        },
      });
  }
}
